import type { Insight } from '@cobliteam/api-lib/src/__generated__/graphql';
import type { Preferences } from '@cobliteam/common-types';
import { useLocalStorage } from '@cobliteam/ui/hooks';
import { subWeeks } from '@cobliteam/utils/date';

const INSIGHTS_WEEK_TTL = 4;

function insightsUtils() {
  const [preferences, setPreferences] = useLocalStorage<Preferences>('preferences');

  const markInsightAsRead = (insight: Insight): { [id: string]: string } => {
    if (!insight) {
      return preferences?.insights ?? {};
    }

    const updatedInsights = {
      ...(preferences?.insights ?? {}),
      [insight.id]: insight.createdAt,
    };

    setPreferences((prev) => ({
      ...prev,
      insights: updatedInsights,
    }));

    return updatedInsights;
  };

  const filterOldInsights = (insights: { [id: string]: string }) => {
    const cutoffDate = subWeeks(new Date(), INSIGHTS_WEEK_TTL);

    const recentInsights = Object.entries(insights).reduce(
      (filtered, [id, createdAt]) => {
        const insightDate = new Date(createdAt);
        if (insightDate > cutoffDate) {
          filtered[id] = createdAt;
        }
        return filtered;
      },
      {} as Record<string, string>
    );

    setPreferences((prev) => ({
      ...prev,
      insights: recentInsights,
    }));

    return recentInsights;
  };

  const getReadInsights = () => {
    return preferences?.insights ?? {};
  };

  const getInsightType = (insight: Insight) => {
    if (insight.driver) {
      return 'driver';
    }

    if (insight.vehicle) {
      return 'vehicle';
    }

    if (insight.group) {
      return 'group';
    }

    return 'general';
  };

  return {
    filterOldInsights,
    getInsightType,
    getReadInsights,
    markInsightAsRead,
  };
}

export default insightsUtils;
